import {Alert, Badge, Group, Paper, Select, SimpleGrid, Stack, Text, TextInput, Title} from "@mantine/core";
import {DataTable, type DataTableSortStatus} from "mantine-datatable";
import {useMemo} from "react";
import {useNavigate} from "react-router";
import Page from "../../components/Page/Page.tsx";
import LoaderScreen from "../../components/Loader/LoaderScreen.tsx";
import useDataTableSessionState from "../../hooks/useDataTableSessionState.ts";
import useVendorsData from "./useVendorsData";
import {getCategoryOrder, getWareCategoryColor, getWareCategoryLabel} from "./utils";
import type {VendorRow, WareCategory} from "./types";

const integerFormat = new Intl.NumberFormat();

const sortRows = <T extends Record<string, unknown>>(rows: T[], sortStatus: DataTableSortStatus<T>): T[] => {
    const accessor = sortStatus.columnAccessor as string;
    const sorted = [...rows].sort((a, b) => {
        const left = a[accessor];
        const right = b[accessor];

        if (typeof left === 'number' || typeof right === 'number') {
            return (typeof left === 'number' ? left : -Infinity) - (typeof right === 'number' ? right : -Infinity);
        }

        return String(left ?? '').localeCompare(String(right ?? ''));
    });

    return sortStatus.direction === 'desc' ? sorted.reverse() : sorted;
};

const renderTypeBadges = (row: VendorRow) => {
    const categories = getCategoryOrder().filter((category) => row.typeCounts[category] > 0);

    if (categories.length === 0) {
        return <Text c="dimmed" size="sm">No wares</Text>;
    }

    return (
        <Group gap={6}>
            {categories.map((category) => (
                <Badge key={category} color={getWareCategoryColor(category)} variant="light">
                    {getWareCategoryLabel(category)} {row.typeCounts[category]}
                </Badge>
            ))}
        </Group>
    );
};

export default function Vendors() {
    const navigate = useNavigate();
    const {vendorRows, stats, isPending, isError, error} = useVendorsData();
    const {sortStatus: vendorSortStatus, setSortStatus: setVendorSortStatus, filters, setFilter} = useDataTableSessionState<
        DataTableSortStatus<VendorRow>,
        {vendorSearch: string; vendorTypeFilter: string}
    >({
        storageKey: 'vendors:list',
        initialSortStatus: {
            columnAccessor: 'name',
            direction: 'asc',
        },
        initialFilters: {
            vendorSearch: '',
            vendorTypeFilter: 'all',
        },
    });
    const vendorSearch = typeof filters.vendorSearch === 'string' ? filters.vendorSearch : '';
    const vendorTypeFilter = typeof filters.vendorTypeFilter === 'string' ? filters.vendorTypeFilter : 'all';

    const vendorTypeOptions = useMemo(() => ([
        {value: 'all', label: 'All types'},
        ...getCategoryOrder().map((category) => ({value: category, label: getWareCategoryLabel(category)})),
    ]), []);

    const filteredVendors = useMemo(() => {
        const search = vendorSearch.trim().toLowerCase();
        return vendorRows.filter((row) => {
            if (vendorTypeFilter !== 'all') {
                const category = vendorTypeFilter as WareCategory;
                if (row.typeCounts[category] === 0) {
                    return false;
                }
            }

            if (!search) {
                return true;
            }

            return row.name.toLowerCase().includes(search) ||
              row.ownerName.toLowerCase().includes(search) ||
              row.location.container?.name.toLowerCase().includes(search) ||
              row.location.sector?.name.toLowerCase().includes(search) ||
              row.location.system?.name.toLowerCase().includes(search);
        });
    }, [vendorRows, vendorSearch, vendorTypeFilter]);

    const sortedVendors = useMemo(() => sortRows(filteredVendors, vendorSortStatus), [filteredVendors, vendorSortStatus]);

    if (isPending) {
        return <LoaderScreen message="Loading vendors" />;
    }

    return (
        <Page>
            <title>Vendors</title>

            <Stack gap="md">
                <div>
                    <Title order={1}>Vendors</Title>
                    <Text c="dimmed">Browse all vendors and their ware type coverage.</Text>
                </div>

                {isError ? (
                    <Alert color="red" title="Unable to load vendors">
                        {error instanceof Error ? error.message : 'Request failed'}
                    </Alert>
                ) : null}

                <SimpleGrid cols={{base: 2, md: 4}} spacing="md">
                    <Paper withBorder p="md" radius="md">
                        <Text c="dimmed" size="sm">Vendors</Text>
                        <Title order={3}>{integerFormat.format(stats.vendorCount)}</Title>
                    </Paper>
                    <Paper withBorder p="md" radius="md">
                        <Text c="dimmed" size="sm">Distinct Wares</Text>
                        <Title order={3}>{integerFormat.format(stats.distinctWares)}</Title>
                    </Paper>
                    <Paper withBorder p="md" radius="md">
                        <Text c="dimmed" size="sm">Ware Listings</Text>
                        <Title order={3}>{integerFormat.format(stats.totalListings)}</Title>
                    </Paper>
                    <Paper withBorder p="md" radius="md">
                        <Text c="dimmed" size="sm">Type Coverage</Text>
                        <Group gap={6} mt="xs">
                            {getCategoryOrder().map((category) => (
                                <Badge key={category} color={getWareCategoryColor(category)} variant="light">
                                    {getWareCategoryLabel(category)} {stats.categoryCounts[category]}
                                </Badge>
                            ))}
                        </Group>
                    </Paper>
                </SimpleGrid>

                <Paper withBorder p="md" radius="md">
                    <Group justify="space-between" mb="md" align="end">
                        <TextInput
                            label="Search vendors"
                            placeholder="Vendor or owner"
                            value={vendorSearch}
                            onChange={(e) => setFilter('vendorSearch', e.currentTarget.value)}
                            style={{flex: 1, minWidth: 220}}
                        />
                        <Select
                            label="Has ware type"
                            data={vendorTypeOptions}
                            value={vendorTypeFilter}
                            onChange={(value) => setFilter('vendorTypeFilter', value ?? 'all')}
                            style={{minWidth: 220}}
                            clearable={true}
                        />
                    </Group>
                    <Text size="sm" c="dimmed" mb="sm">Click a vendor to view details</Text>

                    <DataTable<VendorRow>
                        idAccessor="id"
                        records={sortedVendors}
                        onRowClick={({record}) => navigate(`/vendors/${record.id}`)}
                        sortStatus={vendorSortStatus}
                        onSortStatusChange={setVendorSortStatus}
                        columns={[
                            {accessor: 'name', title: 'Vendor', sortable: true},
                            {accessor: 'ownerName', title: 'Owner', sortable: true},
                            {
                                accessor: 'waresCount',
                                title: '# Wares',
                                sortable: true,
                                render: (row) => integerFormat.format(row.waresCount),
                            },
                            {
                                accessor: 'location.container.name',
                                title: 'Container',
                            },
                            {
                                accessor: 'location.system.name',
                                title: 'Location',
                                render: row => {
                                    return <Stack gap={2}>
                                        <Text size="sm" c="dimmed">{row.location.sector?.name}</Text>
                                        <Text size="sm">{row.location?.system?.name.startsWith('Deep Space') ? row.location?.system?.name : `${row.location?.system?.name} (${row.location?.coordinates?.galaxy?.x}, ${row.location?.coordinates?.galaxy?.y})`}</Text>
                                    </Stack>
                                }
                            },
                            {
                                accessor: 'typeCounts',
                                title: 'Ware Types',
                                render: renderTypeBadges,
                            },
                        ]}
                    />
                </Paper>
            </Stack>
        </Page>
    );
}
